/**
 * videoPlayer.js
 * Custom HTML5 video player with YouTube-style controls
 */

/**
 * SVG icons for video player controls
 * @type {Object<string, string>}
 */
const ICONS = {
  play: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,4 20,12 6,20"></polygon></svg>`,
  pause: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="4" width="4" height="16" rx="1"></rect><rect x="15" y="4" width="4" height="16" rx="1"></rect></svg>`,
  playLarge: `<svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,4 20,12 6,20"></polygon></svg>`,
  volumeHigh: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>`,
  volumeLow: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>`,
  volumeMute: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>`,
  fullscreen: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"></path><path d="M21 8V5a2 2 0 0 0-2-2h-3"></path><path d="M3 16v3a2 2 0 0 0 2 2h3"></path><path d="M16 21h3a2 2 0 0 0 2-2v-3"></path></svg>`,
  exitFullscreen: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"></path><path d="M21 8h-3a2 2 0 0 1-2-2V3"></path><path d="M3 16h3a2 2 0 0 1 2 2v3"></path><path d="M16 21v-3a2 2 0 0 1 2-2h3"></path></svg>`,
  pip: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"></rect><rect x="12" y="9" width="8" height="6" rx="1" fill="currentColor" opacity="0.3"></rect></svg>`,
  spinner: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2a10 10 0 0 1 10 10" opacity="0.9"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg>`,
  retry: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></svg>`,
};

/** Controls auto-hide delay in ms */
const CONTROLS_HIDE_DELAY = 3000;

/** Seek step in seconds for arrow keys */
const SEEK_STEP = 5;

/**
 * Format seconds into MM:SS or HH:MM:SS string
 * @param {number} seconds
 * @returns {string}
 */
function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const sPad = s < 10 ? `0${s}` : `${s}`;
  if (h > 0) {
    const mPad = m < 10 ? `0${m}` : `${m}`;
    return `${h}:${mPad}:${sPad}`;
  }
  return `${m}:${sPad}`;
}

/**
 * Create a custom video player DOM element
 * @param {string} videoSrc - URL of the video source
 * @param {Object} [options={}]
 * @param {string} [options.poster] - Poster image URL
 * @param {string} [options.type] - MIME type (e.g. 'video/mp4')
 * @param {boolean} [options.autoplay=false] - Auto-play on load
 * @param {boolean} [options.muted=false] - Start muted
 * @param {boolean} [options.loop=false] - Loop playback
 * @returns {HTMLElement} The video player container element
 */
export function createVideoPlayer(videoSrc, options = {}) {
  const container = document.createElement('div');
  container.className = 'media-video-player';
  container.setAttribute('tabindex', '0');
  container.setAttribute('role', 'region');
  container.setAttribute('aria-label', 'Video player');

  // --- Video element ---
  const video = document.createElement('video');
  video.className = 'media-video-element';
  video.preload = 'metadata';
  video.playsInline = true;
  if (options.poster) video.poster = options.poster;
  if (options.autoplay) video.autoplay = true;
  if (options.muted) video.muted = true;
  if (options.loop) video.loop = true;

  const source = document.createElement('source');
  source.src = videoSrc;
  if (options.type) source.type = options.type;
  video.appendChild(source);

  // --- Center play overlay ---
  const centerPlay = document.createElement('button');
  centerPlay.className = 'media-video-center-play';
  centerPlay.setAttribute('aria-label', 'Play video');
  centerPlay.innerHTML = ICONS.playLarge;

  // --- Loading spinner ---
  const loader = document.createElement('div');
  loader.className = 'media-video-loader';
  loader.innerHTML = ICONS.spinner;

  // --- Error overlay ---
  const errorOverlay = document.createElement('div');
  errorOverlay.className = 'media-video-error';
  errorOverlay.innerHTML = `
    <div class="media-video-error-msg">Video failed to load</div>
    <button class="media-video-retry-btn" aria-label="Retry">${ICONS.retry}<span>Retry</span></button>
  `;

  // --- PiP indicator ---
  const pipIndicator = document.createElement('div');
  pipIndicator.className = 'media-video-pip-indicator';
  pipIndicator.textContent = 'Playing in Picture-in-Picture';

  // --- Controls bar ---
  const controls = document.createElement('div');
  controls.className = 'media-video-controls';

  // Progress bar
  const progressWrap = document.createElement('div');
  progressWrap.className = 'media-video-progress-wrap';
  progressWrap.setAttribute('role', 'slider');
  progressWrap.setAttribute('aria-label', 'Video progress');
  progressWrap.setAttribute('aria-valuemin', '0');
  progressWrap.setAttribute('aria-valuemax', '100');
  progressWrap.setAttribute('aria-valuenow', '0');

  const progressBuffer = document.createElement('div');
  progressBuffer.className = 'media-video-progress-buffer';

  const progressPlayed = document.createElement('div');
  progressPlayed.className = 'media-video-progress-played';

  const progressThumb = document.createElement('div');
  progressThumb.className = 'media-video-progress-thumb';

  const progressTooltip = document.createElement('div');
  progressTooltip.className = 'media-video-progress-tooltip';

  progressWrap.appendChild(progressBuffer);
  progressWrap.appendChild(progressPlayed);
  progressWrap.appendChild(progressThumb);
  progressWrap.appendChild(progressTooltip);

  // Bottom controls row
  const controlsRow = document.createElement('div');
  controlsRow.className = 'media-video-controls-row';

  // Left controls
  const leftControls = document.createElement('div');
  leftControls.className = 'media-video-controls-left';

  const playBtn = document.createElement('button');
  playBtn.className = 'media-video-btn media-video-play-btn';
  playBtn.setAttribute('aria-label', 'Play');
  playBtn.innerHTML = ICONS.play;

  const volumeWrap = document.createElement('div');
  volumeWrap.className = 'media-video-volume-wrap';

  const volumeBtn = document.createElement('button');
  volumeBtn.className = 'media-video-btn media-video-volume-btn';
  volumeBtn.setAttribute('aria-label', 'Mute');
  volumeBtn.innerHTML = ICONS.volumeHigh;

  const volumeSliderWrap = document.createElement('div');
  volumeSliderWrap.className = 'media-video-volume-slider-wrap';

  const volumeSlider = document.createElement('input');
  volumeSlider.className = 'media-video-volume-slider';
  volumeSlider.type = 'range';
  volumeSlider.min = '0';
  volumeSlider.max = '1';
  volumeSlider.step = '0.05';
  volumeSlider.value = '1';
  volumeSlider.setAttribute('aria-label', 'Volume');

  volumeSliderWrap.appendChild(volumeSlider);
  volumeWrap.appendChild(volumeBtn);
  volumeWrap.appendChild(volumeSliderWrap);

  const timeDisplay = document.createElement('span');
  timeDisplay.className = 'media-video-time';
  timeDisplay.textContent = '0:00 / 0:00';

  leftControls.appendChild(playBtn);
  leftControls.appendChild(volumeWrap);
  leftControls.appendChild(timeDisplay);

  // Right controls
  const rightControls = document.createElement('div');
  rightControls.className = 'media-video-controls-right';

  const pipBtn = document.createElement('button');
  pipBtn.className = 'media-video-btn media-video-pip-btn';
  pipBtn.setAttribute('aria-label', 'Picture in Picture');
  pipBtn.innerHTML = ICONS.pip;
  if (!document.pictureInPictureEnabled) {
    pipBtn.style.display = 'none';
  }

  const fullscreenBtn = document.createElement('button');
  fullscreenBtn.className = 'media-video-btn media-video-fullscreen-btn';
  fullscreenBtn.setAttribute('aria-label', 'Fullscreen');
  fullscreenBtn.innerHTML = ICONS.fullscreen;

  rightControls.appendChild(pipBtn);
  rightControls.appendChild(fullscreenBtn);

  controlsRow.appendChild(leftControls);
  controlsRow.appendChild(rightControls);

  controls.appendChild(progressWrap);
  controls.appendChild(controlsRow);

  // Assemble container
  container.appendChild(video);
  container.appendChild(centerPlay);
  container.appendChild(loader);
  container.appendChild(errorOverlay);
  container.appendChild(pipIndicator);
  container.appendChild(controls);

  // ═══════════════════════════════════════════════════════════
  // STATE & HELPERS
  // ═══════════════════════════════════════════════════════════

  let hideTimer = null;
  let isSeeking = false;
  let wasPlayingBeforeSeek = false;

  function showControls() {
    container.classList.add('media-video-controls-visible');
    clearTimeout(hideTimer);
    if (!video.paused && !video.ended) {
      hideTimer = setTimeout(hideControls, CONTROLS_HIDE_DELAY);
    }
  }

  function hideControls() {
    if (isSeeking) return;
    container.classList.remove('media-video-controls-visible');
  }

  function updatePlayState() {
    if (video.paused || video.ended) {
      playBtn.innerHTML = ICONS.play;
      playBtn.setAttribute('aria-label', 'Play');
      centerPlay.classList.add('media-video-center-play--visible');
      container.classList.add('media-video-controls-visible');
      clearTimeout(hideTimer);
    } else {
      playBtn.innerHTML = ICONS.pause;
      playBtn.setAttribute('aria-label', 'Pause');
      centerPlay.classList.remove('media-video-center-play--visible');
      showControls();
    }
  }

  function updateVolumeIcon() {
    if (video.muted || video.volume === 0) {
      volumeBtn.innerHTML = ICONS.volumeMute;
      volumeBtn.setAttribute('aria-label', 'Unmute');
    } else if (video.volume < 0.5) {
      volumeBtn.innerHTML = ICONS.volumeLow;
      volumeBtn.setAttribute('aria-label', 'Mute');
    } else {
      volumeBtn.innerHTML = ICONS.volumeHigh;
      volumeBtn.setAttribute('aria-label', 'Mute');
    }
    volumeSlider.value = video.muted ? '0' : String(video.volume);
  }

  function updateProgress() {
    if (!video.duration || isSeeking) return;
    const pct = (video.currentTime / video.duration) * 100;
    progressPlayed.style.width = `${pct}%`;
    progressThumb.style.left = `${pct}%`;
    progressWrap.setAttribute('aria-valuenow', String(Math.round(pct)));
    timeDisplay.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
  }

  function updateBuffer() {
    if (!video.duration || !video.buffered.length) return;
    const end = video.buffered.end(video.buffered.length - 1);
    progressBuffer.style.width = `${(end / video.duration) * 100}%`;
  }

  function togglePlay() {
    if (video.paused || video.ended) {
      video.play().catch(() => { /* autoplay blocked */ });
    } else {
      video.pause();
    }
  }

  function toggleMute() {
    video.muted = !video.muted;
    updateVolumeIcon();
  }

  function toggleFullscreen() {
    if (document.fullscreenElement === container) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen().catch(() => { /* denied */ });
    }
  }

  function togglePip() {
    if (document.pictureInPictureElement === video) {
      document.exitPictureInPicture().catch(() => {});
    } else {
      video.requestPictureInPicture().catch(() => {});
    }
  }

  function seekFromProgressEvent(e) {
    const rect = progressWrap.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * (video.duration || 0);
    const pct = ratio * 100;
    progressPlayed.style.width = `${pct}%`;
    progressThumb.style.left = `${pct}%`;
  }

  // ═══════════════════════════════════════════════════════════
  // EVENT BINDINGS
  // ═══════════════════════════════════════════════════════════

  // Video events
  video.addEventListener('play', updatePlayState);
  video.addEventListener('pause', updatePlayState);
  video.addEventListener('ended', updatePlayState);
  video.addEventListener('timeupdate', updateProgress);
  video.addEventListener('progress', updateBuffer);
  video.addEventListener('loadedmetadata', () => {
    timeDisplay.textContent = `0:00 / ${formatTime(video.duration)}`;
    updatePlayState();
  });

  video.addEventListener('waiting', () => {
    loader.classList.add('media-video-loader--visible');
  });
  video.addEventListener('canplay', () => {
    loader.classList.remove('media-video-loader--visible');
  });

  video.addEventListener('error', () => {
    errorOverlay.classList.add('media-video-error--visible');
    loader.classList.remove('media-video-loader--visible');
  });

  // PiP events
  video.addEventListener('enterpictureinpicture', () => {
    pipIndicator.classList.add('media-video-pip-indicator--visible');
    container.classList.add('media-video-pip-active');
  });
  video.addEventListener('leavepictureinpicture', () => {
    pipIndicator.classList.remove('media-video-pip-indicator--visible');
    container.classList.remove('media-video-pip-active');
  });

  // Fullscreen change
  document.addEventListener('fullscreenchange', () => {
    if (document.fullscreenElement === container) {
      fullscreenBtn.innerHTML = ICONS.exitFullscreen;
      fullscreenBtn.setAttribute('aria-label', 'Exit fullscreen');
    } else {
      fullscreenBtn.innerHTML = ICONS.fullscreen;
      fullscreenBtn.setAttribute('aria-label', 'Fullscreen');
    }
  });

  // Retry button
  errorOverlay.querySelector('.media-video-retry-btn').addEventListener('click', () => {
    errorOverlay.classList.remove('media-video-error--visible');
    loader.classList.add('media-video-loader--visible');
    video.load();
    video.play().catch(() => {});
  });

  // Center play overlay
  centerPlay.addEventListener('click', togglePlay);

  // Control buttons
  playBtn.addEventListener('click', togglePlay);
  volumeBtn.addEventListener('click', toggleMute);
  fullscreenBtn.addEventListener('click', toggleFullscreen);
  pipBtn.addEventListener('click', togglePip);

  // Volume slider
  volumeSlider.addEventListener('input', () => {
    video.volume = parseFloat(volumeSlider.value);
    video.muted = video.volume === 0;
    updateVolumeIcon();
  });

  // Progress bar: click to seek
  progressWrap.addEventListener('mousedown', (e) => {
    isSeeking = true;
    wasPlayingBeforeSeek = !video.paused;
    video.pause();
    seekFromProgressEvent(e);
    container.classList.add('media-video-seeking');

    const onMove = (ev) => seekFromProgressEvent(ev);
    const onUp = (ev) => {
      seekFromProgressEvent(ev);
      isSeeking = false;
      container.classList.remove('media-video-seeking');
      if (wasPlayingBeforeSeek) video.play().catch(() => {});
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  });

  // Progress bar: touch support
  progressWrap.addEventListener('touchstart', (e) => {
    isSeeking = true;
    wasPlayingBeforeSeek = !video.paused;
    video.pause();
    const touch = e.touches[0];
    seekFromProgressEvent(touch);
    container.classList.add('media-video-seeking');

    const onMove = (ev) => {
      ev.preventDefault();
      seekFromProgressEvent(ev.touches[0]);
    };
    const onEnd = (ev) => {
      if (ev.changedTouches.length) seekFromProgressEvent(ev.changedTouches[0]);
      isSeeking = false;
      container.classList.remove('media-video-seeking');
      if (wasPlayingBeforeSeek) video.play().catch(() => {});
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, { passive: true });

  // Progress bar: hover tooltip
  progressWrap.addEventListener('mousemove', (e) => {
    if (!video.duration) return;
    const rect = progressWrap.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = ratio * video.duration;
    progressTooltip.textContent = formatTime(time);
    progressTooltip.style.left = `${ratio * 100}%`;
    progressTooltip.classList.add('media-video-progress-tooltip--visible');
  });
  progressWrap.addEventListener('mouseleave', () => {
    progressTooltip.classList.remove('media-video-progress-tooltip--visible');
  });

  // Container hover: show/hide controls
  container.addEventListener('mouseenter', showControls);
  container.addEventListener('mousemove', showControls);
  container.addEventListener('mouseleave', () => {
    if (!video.paused && !isSeeking) {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hideControls, 500);
    }
  });

  // Touch: tap to toggle controls
  let lastTap = 0;
  container.addEventListener('touchend', (e) => {
    // Ignore taps on controls themselves
    if (e.target.closest('.media-video-controls') || e.target.closest('.media-video-center-play')) return;

    const now = Date.now();
    if (now - lastTap < 300) {
      // Double-tap: fullscreen
      toggleFullscreen();
      lastTap = 0;
      return;
    }
    lastTap = now;

    // Single tap: toggle controls visibility
    setTimeout(() => {
      if (lastTap !== 0) {
        if (container.classList.contains('media-video-controls-visible') && !video.paused) {
          hideControls();
        } else {
          showControls();
        }
      }
    }, 310);
  });

  // Double-click: fullscreen (desktop)
  video.addEventListener('dblclick', (e) => {
    e.preventDefault();
    toggleFullscreen();
  });

  // Single click on video: play/pause (desktop)
  video.addEventListener('click', (e) => {
    if (e.detail === 1) {
      // Use timeout to distinguish from double-click
      setTimeout(() => {
        if (e.detail === 1) togglePlay();
      }, 200);
    }
  });

  // Keyboard controls (when container is focused)
  container.addEventListener('keydown', (e) => {
    switch (e.key) {
      case ' ':
      case 'k':
        e.preventDefault();
        togglePlay();
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        toggleMute();
        break;
      case 'f':
      case 'F':
        e.preventDefault();
        toggleFullscreen();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - SEEK_STEP);
        showControls();
        break;
      case 'ArrowRight':
        e.preventDefault();
        video.currentTime = Math.min(video.duration || 0, video.currentTime + SEEK_STEP);
        showControls();
        break;
      case 'ArrowUp':
        e.preventDefault();
        video.volume = Math.min(1, video.volume + 0.1);
        video.muted = false;
        updateVolumeIcon();
        showControls();
        break;
      case 'ArrowDown':
        e.preventDefault();
        video.volume = Math.max(0, video.volume - 0.1);
        updateVolumeIcon();
        showControls();
        break;
      default:
        break;
    }
  });

  // Initialize state
  centerPlay.classList.add('media-video-center-play--visible');
  container.classList.add('media-video-controls-visible');

  return container;
}
